import { diffSets } from '@whiz-cart/node-shared/diffSets';
import { createRef, RefObject, useRef } from 'react';

type OnKeyAddedMeta = {
    /** If `true`, the key was passed to the hook. If `false`, it was added through a proxy fallback. */
    isKnownKey: boolean;
};

type UseManagedRefsOptions<T> = {
    /**
     * If enabled, getting any key will always return a ref, even if the key isn't already known.
     *
     * Example use case:
     * > `react-transition-group` needs a `nodeRef`, or it throws due to a removed method.
     * > But while developing, hot reload can cause keys to be removed before the element is removed.
     */
    enableProxyFallback?: boolean;
    onKeyDeleted?: (key: T) => void;
    onKeyAdded?: (key: T, meta: OnKeyAddedMeta) => void;
};

/**
 * Create a dictionary with a ref for each key, deleted once the respective key no longer exists
 *
 * @param keys Set of keys for which refs should currently exist
 * @returns Dictionary from key to ref
 */
export function useManagedRefs<TRef, TKey extends string | number = string | number>(
    keys: Set<TKey>,
    options: UseManagedRefsOptions<TKey> = {},
): Record<string, RefObject<TRef | null>> {
    const refs = useRef<Record<TKey, RefObject<TRef | null>>>({} as Record<TKey, RefObject<TRef | null>>);
    const proxy = useRef<Record<TKey, RefObject<TRef | null>>>(
        new Proxy(refs.current, {
            get(target, key: string) {
                const existingRef = target[key as TKey];
                if (typeof existingRef !== 'undefined') {
                    return existingRef;
                }

                if (options.enableProxyFallback) {
                    const newRef = createRef<TRef>();
                    target[key as TKey] = newRef;
                    options.onKeyAdded?.(key as TKey, { isKnownKey: false });
                    return newRef;
                }
            },
            set(target, key: string, value: RefObject<TRef | null>) {
                target[key as TKey] = value;
                return true;
            },
        }),
    );

    const previousKeys = useRef<Set<TKey>>(keys);
    // Since we need the ref on the current render pass, this has to happen DURING the render instead of in useEffect or similar
    if (keys !== previousKeys.current) {
        const { added, removed } = diffSets(previousKeys.current, keys);

        for (const key of removed) {
            delete refs.current[key];
            options.onKeyDeleted?.(key);
        }

        for (const key of added) {
            refs.current[key] = createRef<TRef>();
            options.onKeyAdded?.(key, { isKnownKey: true });
        }

        previousKeys.current = keys;
    }

    return proxy.current;
}
